import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MapPin, School, Users, Star, Eye, MessageCircle } from 'lucide-react';
import StarRating from './StarRating';

// Helper function to get region color
const getRegionColor = (region) => {
    const regionColors = {
        'Region 1': '#3B82F6',
        'Region 2E': '#10B981',
        'Region 2W': '#F59E0B',
        'Region 3': '#8B5CF6',
        'Region 4': '#EF4444',
        'Region 5N': '#06B6D4',
        'Region 5S': '#84CC16',
        'Greater Banjul Area': '#3B82F6',
        'West Coast Region': '#10B981',
        'North Bank Region': '#F59E0B',
        'Central River Region': '#8B5CF6',
        'Upper River Region': '#EF4444',
        'Lower River Region': '#06B6D4'
    };
    return regionColors[region] || '#6B7280';
};

const LocationSelectionModal = ({
    isOpen,
    onClose,
    schools,
    onLocationSelect,
    onShowReview,
    onViewReviews,
    schoolStats = {}
}) => {
    if (!isOpen || !schools || schools.length === 0) return null;

    const handleLocationSelect = (school) => {
        onLocationSelect(school); // Navigate map to selected school
        setTimeout(() => onClose(), 150); // Close modal after brief delay for smooth transition
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 bg-black bg-opacity-50 modal-backdrop flex items-center justify-center z-50 p-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
                >
                    {/* Header */}
                    <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                        <div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-1">
                                Select Location
                            </h3>
                            <p className="text-sm text-gray-600">
                                Choose which location of {schools[0].name} you'd like to view
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <X size={20} className="text-gray-500" />
                        </button>
                    </div>

                    {/* Locations List */}
                    <div className="flex-1 overflow-y-auto max-h-96 p-6">
                        <div className="space-y-4">
                            {schools.map((school, index) => (
                                <motion.div
                                    key={school.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="bg-gray-50 rounded-xl p-4 border border-gray-200 hover:border-orange-300 hover:shadow-md transition-all cursor-pointer"
                                    onClick={() => handleLocationSelect(school)}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            {/* Location Header */}
                                            <div className="flex items-center space-x-2 mb-2">
                                                <div
                                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                                    style={{ backgroundColor: getRegionColor(school.region) }}
                                                />
                                                <span className="font-semibold text-gray-900">{school.type}</span>
                                            </div>

                                            {/* Location Details */}
                                            <div className="flex items-center space-x-1 text-sm text-gray-600 mb-3">
                                                <MapPin size={14} />
                                                <span>{school.district}, {school.region}</span>
                                            </div>

                                            {/* School Details */}
                                            <div className="space-y-2 mb-3">
                                                <div className="flex items-center space-x-4 text-sm text-gray-600">
                                                    <div className="flex items-center space-x-1">
                                                        <Users size={14} />
                                                        <span>{school.management}</span>
                                                    </div>
                                                    {school.classification && (
                                                        <div className="flex items-center space-x-1">
                                                            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                                                            <span>{school.classification}</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {school.address && (
                                                    <p className="text-sm text-gray-600 line-clamp-2">
                                                        {school.address}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Rating Section */}
                                            {schoolStats[school.id] && schoolStats[school.id].reviewCount > 0 ? (
                                                <div className="flex items-center space-x-3">
                                                    <div className="text-center">
                                                        <div className="text-lg font-bold text-gray-900">
                                                            {schoolStats[school.id].averageRating.toFixed(1)}
                                                        </div>
                                                        <StarRating
                                                            rating={schoolStats[school.id].averageRating}
                                                            size={14}
                                                            showCount={true}
                                                            reviewCount={schoolStats[school.id].reviewCount}
                                                        />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-sm text-gray-500">No reviews yet</div>
                                            )}
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex flex-col space-y-2 ml-4">
                                            {schoolStats[school.id]?.reviewCount > 0 && (
                                                <button
                                                    onClick={(e) => {
                                                        if (e && typeof e.stopPropagation === 'function') {
                                                            e.stopPropagation();
                                                        }
                                                        onViewReviews(school);
                                                    }}
                                                    className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                                                >
                                                    <Eye size={14} />
                                                    <span>View Reviews</span>
                                                </button>
                                            )}
                                            <button
                                                onClick={(e) => {
                                                    if (e && typeof e.stopPropagation === 'function') {
                                                        e.stopPropagation();
                                                    }
                                                    onShowReview(school);
                                                }}
                                                className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                            >
                                                <MessageCircle size={14} />
                                                <span>Write Review</span>
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="p-6 border-t border-gray-200 bg-gray-50">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-600">
                                Click on a location to view it on the map
                            </p>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default LocationSelectionModal;
